// client/src/components/admin/project/CPSSettlementModal.js
import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

export default function CPSSettlementModal({ open, onClose, projectId, projectNumber }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedView, setSelectedView] = useState('total'); // 'total' | '1' | '2' | '3'...

  // 데이터 조회
  const fetchData = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/projects/${projectId}/cps-settlements`, {
        credentials: 'include'
      });
      const result = await res.json();

      if (result.ok) {
        setData(result);
      } else {
        alert(result.message || '데이터 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('CPS 결산 조회 오류:', error);
      alert('데이터 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open && projectId) {
      fetchData();
      setSelectedView('total'); // 모달 열릴 때마다 총합으로 초기화
    }
  }, [open, projectId, fetchData]);

  if (!open) return null;

  // CPS 미설정
  if (data && !data.hasCPS) {
    return (
      <div className="cps-settlement-modal">
        <div className="cps-settlement-modal__content" onClick={(e) => e.stopPropagation()}>
          <div className="cps-settlement-modal__header">
            <h2 className="cps-settlement-modal__title">CPS 결산 상태</h2>
            <button onClick={onClose} className="cps-settlement-modal__close">✕</button>
          </div>
          <p className="cps-settlement-modal__empty">
            {data.message}
          </p>
          <div className="cps-settlement-modal__footer">
            <button onClick={onClose} className="cps-settlement-modal__button">닫기</button>
          </div>
        </div>
      </div>
    );
  }

  // 현재 선택된 뷰의 데이터
  const currentData = selectedView === 'total' 
    ? data?.total 
    : data?.rounds?.find(r => r.roundNumber === parseInt(selectedView))?.influencers;

  const currentRound = selectedView !== 'total' 
    ? data?.rounds?.find(r => r.roundNumber === parseInt(selectedView))
    : null;

  // 총합 계산
  const totalCustomers = currentData?.reduce((sum, inf) => sum + (inf.customerCount || 0), 0) || 0;
  const totalReward = currentData?.reduce((sum, inf) => sum + (inf.rewardAmount || 0), 0) || 0;

  return (
    <div className="cps-settlement-modal">
      <div className="cps-settlement-modal__content" onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <div className="cps-settlement-modal__header">
          <div>
            <h2 className="cps-settlement-modal__title">CPS 결산 상태</h2>
            <div className="cps-settlement-modal__subtitle">
              프로젝트: {projectNumber}
            </div>
          </div>
          <button onClick={onClose} className="cps-settlement-modal__close">✕</button>
        </div>

        {/* 탭 */}
        <div className="cps-settlement-modal__tabs">
          <button
            onClick={() => setSelectedView('total')}
            className={selectedView === 'total' ? 'cps-settlement-modal__tab--active' : 'cps-settlement-modal__tab'}
          >
            총합
          </button>
          {data?.rounds?.map(round => (
            <button
              key={round.roundNumber}
              onClick={() => setSelectedView(String(round.roundNumber))}
              className={selectedView === String(round.roundNumber) ? 'cps-settlement-modal__tab--active' : 'cps-settlement-modal__tab'}
            >
              {round.roundNumber}회차
            </button>
          ))}
        </div>

        {/* 결산 미완료 경고 */}
        {currentRound && !currentRound.isCompleted && (
          <div className="cps-settlement-modal__warning">
            ⚠️ {currentRound.roundNumber}회차의 결산이 완료 처리되지 않았습니다.
          </div>
        )}

        {/* 자동예약 처리 정보 표시 */}
        {currentRound && currentRound.isCompleted && currentRound.isAutoSettled && (
          <div className="cps-settlement-modal__warning" style={{
            background: currentRound.reservationStatus === 'rejected' ? '#fef2f2' : '#eff6ff',
            border: `1px solid ${currentRound.reservationStatus === 'rejected' ? '#fecaca' : '#bfdbfe'}`,
            color: currentRound.reservationStatus === 'rejected' ? '#991b1b' : '#1e40af',
          }}>
            {currentRound.reservationStatus === 'rejected' ? (
              <>
                <span style={{ fontSize: 16 }}>❌</span>
                <span>미결산으로 관리자 거절 건 (고객 유입 없음)</span>
              </>
            ) : (
              <>
                <span style={{ fontSize: 16 }}>✅</span>
                <span>미결산으로 관리자 승인 건 (최대 고객 수로 지급)</span>
              </>
            )}
            </div>
          )}

        {/* ✅ 증빙 자료 다운로드 섹션 (회차별 탭에서만 표시) */}
        {currentRound && currentRound.proofFiles && currentRound.proofFiles.length > 0 && (
          <div style={{
            padding: '12px 24px',
            background: '#f0f9ff',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap'
          }}>
            <span style={{ fontWeight: 500, color: '#1e40af' }}>📎 결산 증빙 자료:</span>
            {currentRound.proofFiles.map((url, idx) => {
              // ✅ 상대 경로를 절대 URL로 변환 (React Router가 가로채지 않도록)
              const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
              
              return (
                <a
                  key={idx}
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    background: '#3b82f6',
                    color: 'white',
                    borderRadius: 4,
                    fontSize: 13,
                    textDecoration: 'none'
                  }}
                >
                  📥 파일 {idx + 1}
                </a>
              );
            })}
          </div>
        )}

        {/* 테이블 */}
        <div className="cps-settlement-modal__body">
          {loading ? (
            <div className="cps-settlement-modal__loading">로딩 중...</div>
          ) : !currentData || currentData.length === 0 ? (
            <div className="cps-settlement-modal__empty">
              데이터가 없습니다.
            </div>
          ) : (
            <table className="cps-settlement-modal__table">
              <thead>
                <tr>
                  <th>NO</th>
                  <th>인플루언서</th>
                  <th>유입 고객 수</th>
                  <th>지급 포인트</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((inf, index) => (
                  <tr key={inf.influencerId || index}>
                    <td>{index + 1}</td>
                    <td>
                      <div className="cps-settlement-modal__influencer">{inf.influencerName}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        ({inf.influencerUserId})
                      </div>
                    </td>
                    <td className="cps-settlement-modal__number">{inf.customerCount}명</td>
                    <td className="cps-settlement-modal__number">{inf.rewardAmount.toLocaleString()}P</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 총합 */}
        <div className="cps-settlement-modal__summary">
          <div className="cps-settlement-modal__summary-item">
            <span className="cps-settlement-modal__summary-label">총 유입 고객</span>
            <span className="cps-settlement-modal__summary-value cps-settlement-modal__summary-value--primary">
              {totalCustomers}명
            </span>
          </div>
          <div className="cps-settlement-modal__summary-item">
            <span className="cps-settlement-modal__summary-label">총 지급 포인트</span>
            <span className="cps-settlement-modal__summary-value cps-settlement-modal__summary-value--primary">
              {totalReward.toLocaleString()}P
            </span>
          </div>
        </div>

        {/* 총합 설명 (자동예약 건이 있는 경우만 표시) */}
        {data?.rounds?.some(r => r.isAutoSettled) && (
          <div style={{
            padding: '12px 24px',
            background: '#f9fafb',
            borderTop: '1px solid #e5e7eb',
            fontSize: 12,
            color: '#6b7280',
            textAlign: 'center'
          }}>
            💡 총합에는 승인된 자동예약 건만 포함됩니다. 거절된 건은 제외됩니다.
          </div>
        )}

        {/* 닫기 버튼 */}
        <div className="cps-settlement-modal__footer">
          <button onClick={onClose} className="cps-settlement-modal__button">닫기</button>
        </div>
      </div>
    </div>
  );
}

